import { createRootRoute, createRoute, createRouter, Outlet, redirect } from "@tanstack/react-router";
import { LoginPage } from "@/routes/LoginPage";
import { DashboardPage } from "@/routes/DashboardPage";
import { RouteMapPage } from "@/routes/RouteMapPage";
import { TrackPage } from "@/routes/TrackPage";
import { StepPage } from "@/routes/StepPage";
import { CoursePage } from "@/routes/admin/CoursePage";
import { NotebookPage } from "@/routes/NotebookPage";
import { HomeworkPage } from "@/routes/HomeworkPage";
import { NotebooksPage } from "@/routes/NotebooksPage";
import { QuestionsPage } from "@/routes/QuestionsPage";
import { PracticePage } from "@/routes/PracticePage";
import { NotePage } from "@/routes/NotePage";
import { safeReturn } from "@/lib/safeReturn";
import { CheckPage } from "@/routes/CheckPage";
import { MentorQueuePage } from "@/routes/MentorQueuePage";
import { StudentPage } from "@/routes/StudentPage";
import { ReviewPage } from "@/routes/ReviewPage";
import { SubmissionPage } from "@/routes/SubmissionPage";
import { PeoplePage } from "@/routes/PeoplePage";
import { CohortPage } from "@/routes/CohortPage";
import { AnalyticsPage } from "@/routes/AnalyticsPage";
import { NotFound } from "@/ui/states";
import { Shell } from "@/app/Shell";
import { api } from "@/api/client";

// Маршруты объявлены кодом, а не файлами: меньше генерации и один
// источник правды в этом файле (решение по умолчанию, STATUS.md).
const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: () => (
    <Shell>
      <NotFound />
    </Shell>
  ),
});

type LoginSearch = { error?: string; return?: string };

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (s: Record<string, unknown>): LoginSearch => ({
    error: typeof s.error === "string" ? s.error : undefined,
    return: typeof s.return === "string" ? s.return : undefined,
  }),
  // С живой сессией страница входа не нужна — сразу туда, куда шли (или на маршрут).
  beforeLoad: async ({ search }) => {
    const { response } = await api.GET("/me").catch(() => ({ response: { status: 0 } }));
    if (response.status === 200) {
      throw redirect({ href: safeReturn(search.return) });
    }
  },
  component: function Login() {
    const { error, return: ret } = loginRoute.useSearch();
    return <LoginPage error={error} returnTo={ret ?? "/"} />;
  },
});

/** Без сессии — на /login с возвратом. */
async function requireSession({ location }: { location: { href: string } }) {
  const { response } = await api.GET("/me");
  if (response.status === 401) {
    throw redirect({ to: "/login", search: { return: location.href } });
  }
}

const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", beforeLoad: requireSession, component: DashboardPage });
const mapRoute = createRoute({ getParentRoute: () => rootRoute, path: "/map", beforeLoad: requireSession, component: RouteMapPage });
const trackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/track/$slug",
  beforeLoad: requireSession,
  component: function Track() {
    const { slug } = trackRoute.useParams();
    return <TrackPage slug={slug} />;
  },
});
const stepRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/step/$id",
  beforeLoad: requireSession,
  component: function Step() {
    const { id } = stepRoute.useParams();
    return <StepPage id={id} />;
  },
});
const posInt = (v: unknown) => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : undefined;
};
const notebookRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notebook/$assignmentId",
  beforeLoad: requireSession,
  validateSearch: (s: Record<string, unknown>): { task?: number } => ({ task: posInt(s.task) }),
  component: function Notebook() {
    const { assignmentId } = notebookRoute.useParams();
    const { task } = notebookRoute.useSearch();
    return <NotebookPage key={`${assignmentId}:${task ?? ""}`} assignmentId={assignmentId} task={task} />;
  },
});
const checkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notebook/$assignmentId/check",
  beforeLoad: requireSession,
  validateSearch: (s: Record<string, unknown>): { run?: string } => ({ run: typeof s.run === "string" && s.run ? s.run : undefined }),
  component: function Check() {
    const { assignmentId } = checkRoute.useParams();
    const { run } = checkRoute.useSearch();
    return <CheckPage assignmentId={assignmentId} runId={run} />;
  },
});
// SP4: S7 (студент), M1/M2/M3 (ментор).
const submissionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notebook/$assignmentId/review",
  beforeLoad: requireSession,
  validateSearch: (s: Record<string, unknown>): { submission?: string } => ({ submission: typeof s.submission === "string" && s.submission ? s.submission : undefined }),
  component: function Submission() {
    const { assignmentId } = submissionRoute.useParams();
    const { submission } = submissionRoute.useSearch();
    return <SubmissionPage assignmentId={assignmentId} submissionId={submission} />;
  },
});
const mentorQueueRoute = createRoute({ getParentRoute: () => rootRoute, path: "/mentor/queue", beforeLoad: requireSession, component: MentorQueuePage });
const mentorReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mentor/review/$submissionId",
  beforeLoad: requireSession,
  component: function Review() {
    const { submissionId } = mentorReviewRoute.useParams();
    return <ReviewPage submissionId={submissionId} />;
  },
});
const mentorStudentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mentor/student/$userId",
  beforeLoad: requireSession,
  component: function Student() {
    const { userId } = mentorStudentRoute.useParams();
    return <StudentPage userId={userId} />;
  },
});
// SP5: A2, M4, A3.
const peopleRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admin/people", beforeLoad: requireSession, component: PeoplePage });
const analyticsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admin/analytics", beforeLoad: requireSession, component: AnalyticsPage });
const cohortRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mentor/cohort/$scope",
  beforeLoad: requireSession,
  component: function CohortMap() {
    const { scope } = cohortRoute.useParams();
    return <CohortPage scope={scope} />;
  },
});
const noteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notes/$slug",
  beforeLoad: requireSession,
  component: function Note() {
    const { slug } = noteRoute.useParams();
    return <NotePage slug={slug} />;
  },
});
const practiceRoute = createRoute({ getParentRoute: () => rootRoute, path: "/practice", beforeLoad: requireSession, component: PracticePage });
const homeworkRoute = createRoute({ getParentRoute: () => rootRoute, path: "/homework", beforeLoad: requireSession, component: HomeworkPage });
const notebooksRoute = createRoute({ getParentRoute: () => rootRoute, path: "/notebooks", beforeLoad: requireSession, component: NotebooksPage });
const questionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/questions",
  beforeLoad: requireSession,
  validateSearch: (s: Record<string, unknown>): { bank?: string } => ({ bank: typeof s.bank === "string" && s.bank !== "" ? s.bank : undefined }),
  component: function Questions() {
    const { bank } = questionsRoute.useSearch();
    return <QuestionsPage bank={bank} />;
  },
});
const adminCourseRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admin/course", beforeLoad: requireSession, component: () => <CoursePage /> });
const adminStepRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/course/$stepId",
  beforeLoad: requireSession,
  component: function AdminStep() {
    const { stepId } = adminStepRoute.useParams();
    return <CoursePage stepId={stepId} />;
  },
});

const routeTree = rootRoute.addChildren([loginRoute, indexRoute, mapRoute, trackRoute, stepRoute, notebookRoute, checkRoute, submissionRoute, mentorQueueRoute, mentorReviewRoute, mentorStudentRoute, peopleRoute, analyticsRoute, cohortRoute, practiceRoute, noteRoute, homeworkRoute, notebooksRoute, questionsRoute, adminCourseRoute, adminStepRoute]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
